<template>
  <div id="app all-exchanges-search">
    <div v-if="updating" class="loader">
      <div class="loading loading-lg"></div>
    </div>

    <template v-else>
      <div class="scroll-top" @click="scrollToTop">(^)</div>

      <h3><router-link :to="`/everything/`">Search Everything </router-link> > Results</h3>

      <div class="container">
        <div class="input-group columns">
          <span class="col-sm-12 input-group-addon">Search All By</span>
          <select id="category-filter" :class="['form-select field-select col-sm-12', { 'is-error': hasSearchError }]"
            v-model="searchOption">
            <option value>Pick a field</option>
            <option v-for="option in searchOptions" :value="option.id" :key="option.id">
              {{ option.name }}
            </option>
          </select>
          <input class="form-input col-sm-12" type="text" id="search-for-something" placeholder="for..."
            v-model="sstring" @keydown.enter="search" />
          <button class="btn btn-primary input-group-btn" @click="search">
            Go
          </button>
        </div>
      </div>

      <label class="form-switch form-inline">
        <input type="checkbox" v-model="limit" />

        <i class="form-icon"></i> Limit to exchanges due within the last 365 days<span class="warn tooltip"
          data-tooltip="Will exclude anything that has no
due date at all, and exchanges
scraped before a certain time.">
          <sup>
            <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
          </sup>
        </span>
      </label>

      <label class="form-switch form-inline">
        <input type="checkbox" v-model="toggle" />
        <i class="form-icon"></i> Use toggles for prompt text
      </label>

      <div class="divider"></div>
      <!-- FILTERS -->

      <div class="accordion">
        <input type="checkbox" id="accordion-1" name="accordion-checkbox" hidden />
        <label class="accordion-header c-hand" for="accordion-1">
          <i class="icon icon-arrow-right mr-1"></i>
          <strong>Quick Filter This Page</strong>
          <span v-if="filterCount > 0">
            ({{ filterCount }} applied, filtered to {{ filtered.length }} of {{ prompts.length }} on
            this page out of {{ totalRequests }} total)
          </span>
        </label>
        <div class="accordion-body">
          <!-- Accordions content -->
          <div class="filters" role="search">
            <div class="form-horizontal columns">
              <div class="form-group column col-4">
                <div class="col-5 col-sm-12">
                  <label class="form-label" for="prompt-filter">Prompt: </label>
                </div>
                <div class="col-7 col-sm-12">
                  <input class="form-input" type="text" id="prompt-filter" placeholder="Search prompt text..."
                    v-model="promptFilter" />
                </div>
              </div>

              <div class="form-group column col-4">
                <div class="col-5 col-sm-12">
                  <label class="form-label" for="freeform-filter">Freeforms: </label>
                </div>
                <div class="col-7 col-sm-12">
                  <input class="form-input" type="text" id="freeform-filter" placeholder="Search freeforms..."
                    v-model="freeformFilter" />
                </div>
              </div>
              <div class="form-group column col-4">
                <div class="col-5 col-sm-12">
                  <label class="form-label" for="optional-tags-filter">Optional Tags: </label>
                </div>
                <div class="col-7 col-sm-12">
                  <input class="form-input" type="text" id="optional-tags-filter" placeholder="Search optional tags..."
                    v-model="optionalTagFilter" />
                </div>
              </div>
            </div>

            <div class="form-horizontal columns">
              <div class="form-group column col-4">
                <div class="col-5 col-sm-12">
                  <label class="form-label" for="character-filter">Characters: </label>
                </div>
                <div class="col-7 col-sm-12">
                  <input class="form-input" type="text" id="character-filter" placeholder="Search characters..."
                    v-model="characterFilter" />
                </div>
              </div>
              <div class="form-group column col-4">
                <div class="col-5 col-sm-12">
                  <label class="form-label" for="relationship-filter">Relationships: </label>
                </div>
                <div class="col-7 col-sm-12">
                  <input class="form-input" type="text" id="relationship-filter" placeholder="Search relationships..."
                    v-model="relationshipFilter" />
                </div>
              </div>
              <div class="form-group column col-4" v-if="view != 'everything-fandom'">
                <div class="col-5 col-sm-12">
                  <label class="form-label" for="fandom-filter">Fandom: </label>
                </div>
                <div class="col-7 col-sm-12">
                  <input class="form-input" type="text" id="fandom-filter" placeholder="Search fandoms..."
                    v-model="fandomFilter" />
                </div>
              </div>
            </div>
            <div class="mt-2 mb-2">
              <small>Tip: search terms are AND by default. You can use syntax like | for OR, ! for NOT,
                and ="exact match".</small>
            </div>
          </div>

          <!-- END ACC -->
        </div>
      </div>

      <!-- END FILTERS -->
      <pagination :pagination="pagination" @page="page"></pagination>

      <div class="pl-2">
        <small>Sorted by due date, then last scraped, then fandom. Exchanges with no due dates available
          are shown last.</small>
      </div>
      <table class="main">
        <thead>
          <tr>
            <th class="prompts">Requests</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(prompt, index) in filtered" :key="'p' + prompt.id + index" :class="{ odd: index % 2 !== 0 }">
            <td class="prompts">
              <div class="prompt">
                <div class="user-data">
                  <strong>
                    <button aria-label="Toggle prompt bookmark" v-if="!isExchangeOpen(prompt.exchange)" class="bookmark"
                      @click="toggleEverythingBookmark(prompt)">
                      <span aria-label="Remove bookmark" v-if="hasEverythingBookmark(prompt)"
                        class="fas fa-heart"></span>
                      <span v-else class="far fa-heart" aria-label="Bookmark"></span>
                    </button>

                    <a href="#" @click.prevent="
                      $router.push({
                        path: `/everything/username/${prompt.username}`
                      })
                      ">{{ prompt.username }}</a>
                    -

                    <router-link :to="`/${prompt.exchange}`">{{ prompt.exchange }}</router-link>
                  </strong>
                </div>

                <div class="flex">
                  <div class="requested-characters">
                    <strong>Characters & Relationships &nbsp;</strong>
                    <template v-if="prompt.relationships">
                      <ul class="characters">
                        <li v-for="(c, i) in prompt.relationships" :key="`${prompt.id}-${c}-${i}`">
                          {{ c }}
                        </li>
                      </ul>
                    </template>
                    <template v-if="prompt.characters">
                      <hr v-if="prompt.relationships" class="mt-2 mb-2" />
                      <ul class="characters">
                        <li v-for="(c, i) in prompt.characters" :key="`${prompt.id}-${c}-${i}`">
                          {{ c }}
                        </li>
                      </ul>
                    </template>
                    <div v-if="!prompt.relationships && !prompt.characters">
                      None
                    </div>
                  </div>

                  <div>
                    <div class="mb-2">
                      <div class="mb-2" v-if="prompt.fandom">
                        <strong>Fandom:</strong>&nbsp;
                        <router-link :to="`/everything/fandom/${encodeURIComponent(prompt.fandom)}`">{{ prompt.fandom
                        }}</router-link></span>
                      </div>
                      <div class="mb-2" v-if="prompt.fandom">
                        <strong>Exchange Closes Around:</strong>&nbsp;
                        <span>{{
                          exchangeDate(prompt.exchange, "due_date")
                            ? new Date(exchangeDate(prompt.exchange, "due_date")).toDateString()
                            : "--"
                        }}</span>
                      </div>
                      <div class="mb-2" v-if="prompt.fandoms && prompt.fandoms.length > 1">
                        <strong>Other Fandoms:</strong>
                        <span class="chip" :key="prompt.id + prompt.username + p + i"
                          v-for="(p, i) in prompt.fandoms.filter((f) => f != prompt.fandom)">{{ p }}</span>
                      </div>
                      <div class="mb-2" v-if="prompt.title">
                        <strong>Title:</strong>&nbsp;
                        <span>{{ prompt.title }}</span>
                      </div>

                      <div class="mb-2" v-if="prompt.warnings">
                        <strong>Archive Warnings:</strong>
                        <span class="chip" :key="prompt.id + prompt.username + p" v-for="p in prompt.warnings">{{ p
                        }}</span>
                      </div>
                      <div class="mb-2" v-if="prompt.ratings">
                        <strong>Ratings:</strong>
                        <span class="chip" :key="prompt.id + prompt.username + p" v-for="p in prompt.ratings">{{ p
                        }}</span>
                      </div>

                      <div class="mb-2" v-if="prompt.freeforms">
                        <strong>Freeforms:</strong>
                        <span class="chip" :key="prompt.id + prompt.username + f + i"
                          v-for="(f, i) in prompt.freeforms">{{ f }}</span>
                      </div>

                      <div v-if="prompt.optionalTags || prompt.optional_tags" class="mb-2">
                        <strong>Optional Tags:</strong>
                        <span class="chip" :key="prompt.id + prompt.username + f + i"
                          v-for="(f, i) in prompt.optional_tags">{{ f
                          }}</span>
                      </div>
                    </div>
                    <div class="mb-2" v-if="prompt.letter && prompt.letter.length">
                      <a :href="prompt.letter" target="blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                        <sup> <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
                    </div>

                    <button class="btn btn-link" v-if="toggle && prompt.prompt" @click="togglePrompt(prompt.sha)">
                      <span v-if="shouldShowPrompt(prompt.sha)">
                        <i class="icon icon-arrow-down mr-1"></i> Hide prompt
                      </span>
                      <span v-else> <i class="icon icon-arrow-right mr-1"></i> Show prompt </span>
                    </button>
                    <template v-if="(!toggle || shouldShowPrompt(prompt.sha)) && prompt.prompt">
                      <div v-html="prompt.prompt" class="prompt-text"></div>
                    </template>

                    <p v-else-if="!prompt.prompt">
                      No optional details given.
                      <span v-if="prompt.letter">
                        However, they do have a
                        <a :href="prompt.letter" target="blank">letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                          <sup>
                            <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>.
                      </span>
                    </p>
                  </div>
                </div>

                <hr class="break" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <pagination :pagination="pagination" @page="page"></pagination>
    </template>
  </div>
</template>

<script>
import { find, includes, debounce, intersection, orderBy, sortBy, each, remove } from "lodash-es";
import utils from "../components/utils.js";
import Pagination from "../components/pagination.vue";
import { mapGetters } from "vuex";
import axios from "axios";
import Fuse from "fuse.js";

let fandomSearch;
let freeformSearch;
let relationshipSearch;
let optionalTagsSearch;
let charactersSearch;
let promptSearch;

// Remove english articles from fandom names
function removeArticlesCompare(o) {
  if (!o) {
    return;
  }
  const regex = /^(the\s|a\s|an\s)/i;
  if (!o.name) {
    return o;
  }
  return o.name.toLowerCase().replace(regex, "");
}

function normalizeStr(str) {
  if (!str || typeof str !== "string") {
    return;
  }
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

const searchOptions = [
  {
    name: "Character/Relationship",
    id: "individual"
  },
  {
    name: "Fandom",
    id: "fandom"
  },
  {
    name: "Freeform",
    id: "freeform"
  },
  {
    name: "Prompt",
    id: "prompt"
  },
  {
    name: "Username (Exact)",
    id: "user"
  }
];

export default {
  name: "everythingSearch",
  components: {
    Pagination
  },
  created() {
    this.$store.commit("setExchangeId", "");
    this.load();
  },
  destroyed() { },
  data() {
    return {
      updating: true,
      pagination: {
        first: 0,
        last: 100,
        size: 100,
        total: 0
      },
      showPrompt: [],
      fandom: "",
      user: "",
      prompts: [],
      filtered: [],
      sstring: "",
      view: "",
      limit: false,
      toggle: true,
      everythingFilter: "",
      everythingFiltered: [],
      totalRequests: 0,
      hasSearchError: false,
      fandomFilter: undefined,
      freeformFilter: undefined,
      relationshipFilter: undefined,
      optionalTagFilter: undefined,
      characterFilter: undefined,
      promptFilter: undefined,
      searchOption: "",
      searchOptions,
      totalFound: 0
    };
  },
  computed: {
    ...mapGetters(["exchanges", "exchangeDueDates"]),
    filterCount() {
      let count = 0;

      if (this.fandomFilter && this.fandomFilter.length) {
        count++;
      }
      if (this.freeformFilter && this.freeformFilter.length) {
        count++;
      }
      if (this.relationshipFilter && this.relationshipFilter.length) {
        count++;
      }
      if (this.optionalTagFilter && this.optionalTagFilter.length) {
        count++;
      }
      if (this.characterFilter && this.characterFilter.length) {
        count++;
      }
      if (this.promptFilter && this.promptFilter.length) {
        count++;
      }
      return count;
    }
  },
  watch: {
    $route(to, from) {
      if (to.name == "everything-search") {
        this.$store.commit("setExchangeId", "");
        this.load(to.params, from.params);
      }
    },
    everythingFilter() {
      this.updateEverythingFandoms();
    },
    fandomFilter() {
      this.updateFilter();
    },
    characterFilter() {
      this.updateFilter();
    },
    freeformFilter() {
      this.updateFilter();
    },
    optionalTagFilter() {
      this.updateFilter();
    },
    relationshipFilter() {
      this.updateFilter();
    },
    promptFilter() {
      this.updateFilter();
    },
    limit() {
      this.load();
    }
  },
  methods: {
    hasEverythingBookmark: utils.hasEverythingBookmark,
    toggleEverythingBookmark: utils.toggleEverythingBookmark,
    removeEverythingBookmark: utils.removeEverythingBookmark,
    exchangeDate: utils.exchangeDate,
    isExchangeOpen(ex) {
      if (this.exchanges) {
        var meta = find(this.exchanges, (e) => {
          return e.name == ex;
        });

        if (meta) {
          return meta.isOpen;
        }

        return false;
      }
    },
    search() {
      if (!this.searchOption) {
        this.hasSearchError = true;
        return;
      }

      if (!this.sstring || !this.sstring.length) return;

      this.hasSearchError = false;
      if (this.searchOption == "user") {
        this.$router.push({
          path: `/everything/username/${encodeURIComponent(this.sstring)}`
        });
      } else {
        this.$router.push({
          path: `/everything/search/${this.searchOption}/${encodeURIComponent(this.sstring)}`
        });
      }
    },
    load(to, from) {
      const isDifferentOption = from && to && from.sfield !== to.sfield;
      this.updating = true;

      this.prompts = [];
      this.filtered = [];
      this.showPrompt = [];

      this.sstring = this.$route.params.sstring;
      this.searchOption = this.$route.params.sfield;

      // reset pagination if we're switching views
      if (isDifferentOption) {
        this.pagination.first = 0;
        this.pagination.last = this.pagination.size = 100;
      }

      if (!this.sstring || !this.sstring.length) {
        this.prompts = [];
        this.filtered = [];
        this.updating = false;
        return;
      }

      if (this.searchOption == "user") {
        axios
          .get(
            `https://autoao3.herokuapp.com/db/user/${encodeURIComponent(this.sstring)}${this.limit ? "?limit=true" : ""
            }`
          )
          .then((res) => {
            this.prompts = sortBy(res.data, (f) => f.name, [removeArticlesCompare]);
            this.totalRequests = this.prompts.length;
            this.pagination.total = this.totalRequests;
            // we show all prompts for a user
            this.pagination.last = this.pagination.size = this.totalRequests;
            this.setupFilters(this.prompts);
            this.updateFilter();
          })
          .catch(() => {
            this.prompts = [];
            this.filtered = [];
            this.totalRequests = undefined;
            this.pagination.total = this.totalRequests;
            this.updating = false;
          });

        return;
      }

      axios
        .get(
          `https://autoao3.herokuapp.com/db/search/${this.searchOption}/${encodeURIComponent(
            this.sstring
          )}?${this.limit ? "limit=true" : ""}&start=${this.pagination.first}&end=${this.pagination.last
          }`
        )
        .then((res) => {
          this.prompts = [];
          this.prompts = res.data;
          this.totalRequests = res.data.length ? Number(res.data[0]["total_requests"]) : 0;

          this.pagination.size = 100;
          this.pagination.total = this.totalRequests;
          this.setupFilters(this.prompts);
          this.updateFilter();
        })
        .catch(() => {
          this.prompts = [];
          this.filtered = [];
          this.totalRequests = undefined;
          this.pagination.total = undefined;
          this.updating = false;
        });
    },
    setupFilters(result) {
      const fuseOptions = {
        shouldSort: true,
        threshold: 0,
        ignoreLocation: true,
        useExtendedSearch: true,
        getFn(obj, path) {
          //   // Use the default `get` function
          const value = Fuse.config.getFn(obj, path);
          //   // ... do something with `value`

          if (Array.isArray(value)) {
            return value.map((el) => normalizeStr(el));
          }
          return normalizeStr(value);
        }
      };
      fandomSearch = new Fuse(result, {
        keys: ["fandoms", "fandom"],
        ...fuseOptions
      });
      relationshipSearch = new Fuse(result, {
        keys: ["relationships"],
        ...fuseOptions
      });
      freeformSearch = new Fuse(result, {
        keys: ["freeforms"],
        ...fuseOptions
      });

      charactersSearch = new Fuse(result, {
        keys: ["characters"],
        ...fuseOptions
      });

      optionalTagsSearch = new Fuse(result, {
        keys: ["optionalTags"],
        ...fuseOptions
      });

      promptSearch = new Fuse(result, {
        keys: ["prompt"],
        ...fuseOptions
      });
    },

    updateFilter: debounce(function () {
      this.updating = true;
      let arr = this.prompts;

      if (this.fandomFilter && this.fandomFilter.length) {
        const indices = fandomSearch.search(normalizeStr(this.fandomFilter));
        const res = [];
        each(indices, (i) => {
          res.push(this.prompts[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.relationshipFilter && this.relationshipFilter.length) {
        const indices = relationshipSearch.search(normalizeStr(this.relationshipFilter));
        const res = [];
        each(indices, (i) => {
          res.push(this.prompts[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.characterFilter && this.characterFilter.length) {
        const indices = charactersSearch.search(normalizeStr(this.characterFilter));
        const res = [];
        each(indices, (i) => {
          res.push(this.prompts[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.optionalTagFilter && this.optionalTagFilter.length) {
        const indices = optionalTagsSearch.search(normalizeStr(this.optionalTagFilter));
        const res = [];
        each(indices, (i) => {
          res.push(this.prompts[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.freeformFilter && this.freeformFilter.length) {
        const indices = freeformSearch.search(normalizeStr(this.freeformFilter));
        const res = [];
        each(indices, (i) => {
          res.push(this.prompts[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.promptFilter && this.promptFilter.length) {
        const indices = promptSearch.search(normalizeStr(this.promptFilter));
        const res = [];
        each(indices, (i) => {
          res.push(this.prompts[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      arr = orderBy(
        arr,
        [
          (p) =>
            this.exchangeDate(p.exchange, "due_date") || this.exchangeDate(p.exchange, "timestamp"),
          (p) => this.exchangeDate(p.exchange, "timestamp"),
          "username"
        ],
        ["desc", "desc", "asc"]
      );

      this.totalFound = arr.length;

      this.filtered = arr;

      this.updating = false;
    }, 300),
    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    togglePrompt(sha) {
      if (this.shouldShowPrompt(sha)) {
        this.showPrompt = remove(this.showPrompt, sha);
      } else {
        this.showPrompt.push(sha);
      }
    },
    shouldShowPrompt(sha) {
      return includes(this.showPrompt, sha);
    },
    async page(start) {
      this.pagination.last = start + this.pagination.size;
      this.pagination.first = start;
      this.load();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";

.field-select {
  flex: 0 1 auto;
  width: 33%;
}

.prompt {
  margin: 0.8em 0;
  // border-bottom: 1px solid $outline-light;

  &:last-child {
    .break {
      display: none;
    }
  }
}

.bookmark,
.letter {
  height: 1.3em;
}

.user-data {
  font-size: larger;

  .letter {
    padding: 0;
    margin: 0;
    vertical-align: baseline;
  }

  .bookmark {
    position: relative;
    bottom: 4px;
    margin-right: 0.4em;
  }
}

.flex {
  margin-top: 0.4em;
  display: flex;
}

ul {
  li {
    margin: 0;
    padding: 4px 0;
    border-bottom: 1px solid $outline-light;

    &:last-child {
      border: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }
}

.requested-characters {
  // margin-top: 1em;
  margin-right: 1em;
  flex: 0 0 25%;
}

.toggle {
  width: 150px;
  height: 35px;
  display: block;
}

.break {
  width: 80%;
  margin: 0.8em auto;
}

@media screen and (max-width: 767px) {
  .flex {
    display: inherit;
  }

  .requested-characters {
    display: block;
    margin-bottom: 0.5em;
  }
}
</style>
